import merge from 'lodash/merge.js';

import axios from '@/plugins/axios.js';
import GradebookGrade from '@/store/modules/gradebook/GradebookGrade.js';
import Collection from '@/store/quill/Collection.js';
import { promiseError, toggleLoading } from '@/store/utilities/Helpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default merge(Collection('GradebookGrades', GradebookGrade), {
    state: {},

    getters: {},

    actions: {
        async fetchForStudent(context, { activityId, courseId }) {
            const { dispatch, rootState } = context;

            toggleLoading(context);

            const studentId = rootState.Auth.user.id;

            const queryBuilder = new QueryBuilder('gradebook-grades');
            queryBuilder
                .filters([
                    { key: 'user_id', value: studentId },
                    { key: 'activity_id', value: activityId },
                ])
                .raw({ course_id: courseId });

            try {
                const { data } = await axios.get(queryBuilder.toUrl());

                return Promise.all([dispatch('updatePageMeta', data.meta), dispatch('hydrate', data.data)]);
            } catch (error) {
                promiseError({
                    key: 'gradebook_grades_for_student_load_error',
                    context,
                });

                // ActivityView keeps the activity body hidden until this resolves, so a
                // failure must reject rather than look like an empty grade list.
                throw error;
            } finally {
                toggleLoading(context);
            }
        },

        async fetchRunningGrades(context, { courseId, userId }) {
            toggleLoading(context);

            try {
                const runningGradesApi = await axios.get(
                    new QueryBuilder('running-course-grades')
                        .filters([
                            { key: 'course_id', value: courseId },
                            { key: 'user_id', value: userId },
                        ])
                        .raw({ for_profile: 1 })
                        .toUrl()
                );

                const gradeInfo = runningGradesApi.data.data ? runningGradesApi.data.data : runningGradesApi.data;

                // Profile requests always return toRenderRunningGrade format
                if (gradeInfo.course_id && !Array.isArray(gradeInfo)) {
                    return [
                        {
                            course_id: gradeInfo.course_id,
                            running_percent: gradeInfo.running_percent,
                            running_grade: gradeInfo.running_grade,
                        },
                    ];
                }

                // Handle array of running grades (multiple courses)
                if (Array.isArray(gradeInfo)) {
                    return gradeInfo.map(summary => {
                        if (summary.course_id) {
                            return {
                                course_id: summary.course_id,
                                running_percent: summary.running_percent,
                                running_grade: summary.running_grade,
                            };
                        }
                        return {};
                    });
                }

                // Fallback for unexpected format
                return [];
            } finally {
                toggleLoading(context);
            }
        },
    },

    mutations: {},
});
